import { useI18nContext } from "@/hooks/useI18nContext";
import {
  AB_TESTING_ID,
  FIRST_PROMOTER_KEY,
  FIRST_PROMOTER_KEY_US,
  GA_KEY,
  GTM_ID,
  RECAPTCHA_TOKEN,
} from "@/shared/constants/env/externalService.constants";
import {
  GOOGLE_ADS_CONVERSION_ID_INDIA,
  G_TAG,
} from "@/shared/constants/env/google.constants";
import { ROOT_HOST } from "@/shared/constants/env/internalService.constants";
import { IS_TIMEZONE_INDIA } from "@/shared/constants/timezone.constants";
import { useDataStore } from "@/shared/store/useDataStore";
import useModalStore from "@/shared/store/useModalStore";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { useEffect } from "react";
import ReactGA from "react-ga";
import TagManager from "react-gtm-module";
// import { Intercom, LiveChatLoaderProvider } from "react-live-chat-loader";

const CHAT_HIDDEN_PATHS = new Set(["/lms"]);

const CustomScripts = () => {
  const { openSignUp } = useModalStore();

  const path = usePathname();
  const isUsLocale = useI18nContext().locale === "us";
  // CMS marketing pages no longer carry a "/page/" URL prefix; the
  // [slug].tsx dispatcher publishes the active slug into the data store.
  const isMarketingPage = !!useDataStore((s) => s.marketingPageSlug);
  const isSignupOrSigninPage = path?.includes("/signup") || path?.includes("/signin");
  // Landing pages that should not show the Freshchat widget.
  const isChatHiddenPage = CHAT_HIDDEN_PATHS.has(path?.replace(/\/$/, "") ?? "");
  // Pages that call grecaptcha.execute() at top level (not behind a
  // user-triggered modal). These need reCAPTCHA available on mount.
  const needsRecaptchaEagerly =
    isSignupOrSigninPage || path?.includes("/google/");

  const returnHead = () => {
    return (
      <>
        {/* reCAPTCHA was previously loaded on every page (370 KB +
            ~450 ms scripting time) but only signin/signup flows use it.
            On signin/signup pages we still load it eagerly so the form
            is ready. Everywhere else, SignInModal injects it on first
            mount so it doesn't pay the cost on Lighthouse runs of
            content pages. */}
        {needsRecaptchaEagerly && (
          <Script
            strategy="lazyOnload"
            src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_TOKEN}`}
          />
        )}
        {/* Microsoft Clarity — moved here from _document.tsx so it loads
            after the page is interactive instead of blocking FCP/LCP. */}
        <Script id="ms-clarity" strategy="lazyOnload">
          {`(function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "v0pvm1n9ic");`}
        </Script>
        {/* Facebook Pixel — India locale only, lazy-loaded so it doesn't
            block first paint and so its third-party cookies don't show up
            inside Lighthouse's measurement window. */}
        {!isUsLocale && (
          <Script id="fb-pixel" strategy="lazyOnload">
            {`!function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '371165273874242');
            fbq('track', 'PageView');`}
          </Script>
        )}
      </>
    );
  };
  useEffect(() => {
    try {
      TagManager.initialize({
        gtmId: GTM_ID,
        dataLayerName: G_TAG.ROOT_DATA_LAYER,
      });
    } catch (err) {
      // Log TagManager fail message
    }
  }, []);

  useEffect(() => {
    ReactGA.initialize(GA_KEY);
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, []);
  

  const firstpromoterScript  = ()=>{
    let cid = FIRST_PROMOTER_KEY;
    if(isUsLocale){
      cid = FIRST_PROMOTER_KEY_US;
    }
    return (`(function(){var t=document.createElement("script");
    t.type="text/javascript",t.async=!0,t.src='https://cdn.firstpromoter.com/fprom.js',t.onload=t.onreadystatechange=function(){var t=this.readyState;if(!t||"complete"==t||"loaded"==t)try{$FPROM.init("${cid}",".${ROOT_HOST}")}catch(t){}};var e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(t,e)})();`);
  }

  return (
    <>
      {returnHead()}
      {IS_TIMEZONE_INDIA && (
        <Script
          strategy="lazyOnload"
          src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_CONVERSION_ID_INDIA}`}
        ></Script>
      )}
      {AB_TESTING_ID && (
        <Script
          strategy="lazyOnload"
          src={`https://www.googleoptimize.com/optimize.js?id=${AB_TESTING_ID}`}
        />
      )}
      <Script
        strategy="lazyOnload"
        id="firstpromoter"
        dangerouslySetInnerHTML={{
          __html: firstpromoterScript(),
        }}
      />

      {/* <LiveChatLoaderProvider providerKey="flrgjjbv" provider="intercom">
        {!openSignUp ? <Intercom /> : null}
      </LiveChatLoaderProvider> */}

      {!isUsLocale && !isMarketingPage && !isSignupOrSigninPage && !isChatHiddenPage && (
      //    <Script
      //    strategy="lazyOnload"
      //    id="bot9-chat"
      //    dangerouslySetInnerHTML={{
      //      __html: `
      //      BOT9_DATA = {
      //        bot9Id: "8d18216e-8261-4ef1-850e-37c088a3600d",
      //        // user: {
      //        //   userId: '<unique_user_id>',
      //        //   emailId: '<user_email>',
      //        //   name: '<user_name>',
      //        //   customAttributes: {
      //        //     Field1: '',
      //        //     Field2: '',
      //        //     Field3: '',
      //        //   }
      //        // }
      //      };
      //      document.head.appendChild(document.createElement('script')).src = 'https://sdk.chatwidget.in/bot.js';`
      //    }}
      //  />
        <Script strategy="lazyOnload" id="freshchat-widget">
          {`
            window.prechatTemplate = {
              "SubmitLabel": "Continue",
              "fields": {
                "0": {
                  "error": "Please Enter a valid name",
                  "fieldId": "name",
                  "label": "Name",
                  "required": "yes",
                  "type": "text"
                },
                "1": {
                  "error": "Please Enter a valid Email",
                  "fieldId": "email",
                  "label": "Email",
                  "required": "yes",
                  "type": "email"
                },
                "2": {
                  "error": "Please Enter a valid Phone Number",
                  "fieldId": "phone",
                  "label": "Phone",
                  "required": "no",
                  "type": "phone"
                  },
                  "3": {
                  "error": "Please select an option",
                  "fieldId": "alreadyCustomer",
                  "label": "Already a Graphy customer?",
                  "required": "yes",
                  "type": "dropdown",
                  "options": ["Yes", "No"]
                  }
              },
              "heading": "",
              "mainbgColor": "#0AA4DB",
              "maintxColor": "#fff",
              "textBanner": "Welcome to Graphy! Before we get started, please fill in these details"
            };

            window.fcSettings = {
              onInit: function() {
                window.fcPreChatform.fcWidgetInit(window.prechatTemplate);
              }
            };

            window.fcWidgetMessengerConfig = {
              config: {
                content: {
                  actions: {
                    tab_chat: "Chat"
                  },
                  headers: {
                    chat: "Chat With Us"
                  }
                },
                cssNames: {
                  expanded: "custom_fc_expanded",
                  widget: "custom_fc_frames"
                }
              }
            };

            const freshChatScript = document.createElement('script');
            freshChatScript.src = '//in.fw-cdn.com/32264024/1216192.js';
            freshChatScript.setAttribute('chat', 'true');
            document.head.appendChild(freshChatScript);

            const preChatScript = document.createElement('script');
            preChatScript.src = 'https://snippets.freshchat.com/js/fc-pre-chat-form-v2.js';
            document.head.appendChild(preChatScript);

            let flag = false;
            let intval;
            intval = setInterval(() => {
              let a = document.querySelectorAll('p.fc-header');
              if (a.length > 0 && flag === false) {
                let c = document.createElement('img');
                c.id = "myImage";
                c.src = "https://bijoyndevasia5114.ongraphy.com/s/pages/assets/images/Logo_White.svg";
                c.width = 50;
                c.height = 50;
                let g = a[0];
                g.appendChild(c);
                flag = true;
                clearInterval(intval);
              }
            }, 400);
          `}
        </Script>
      )}

<style jsx global>{`

@media (max-width: 767px) { /* Adjust breakpoint as needed */
          .fc-header {
            height: 100;
            font-family: sans-serif;

          } 
          .chat-fc-form-outer{
            
            }

          .chat-fc-form-outer div.fc-form{
                  padding-top: 30px;
          }

       
          .chat-fc-form-outer p {
            padding-top: 15px !important; /* Adjust padding */
            font-size: 20px; /* Smaller font size */
            font-family: sans-serif;
          }
          .chat-fc-form-outer div.fc-form ul li {
            margin-bottom: 15px !important; /* Reduce margin */
            
            
          }

          // .chat-fc-form .pre-fc-field input{
          // height:48px
          // }
           .chat-fc-form-outer div.fc-form ul li input {
           height: 58px;
            padding: 8px !important; /* Adjust padding */
            font-size: 24px; /* Smaller font size */
            margin-bottom: 20px
            
          }
          .chat-fc-form-outer div.fc-form a.fc-button {
          border-radius: 15px;
            font-size: 18px;
        padding: 15px 15px;
        min-height: 48px;
        align-content: center;
        margin-top: 75px;
        
        
          }
          #myImage{
            width: 40px;
            height: 40px;
          }
        }

        
        /* Desktop Styles (Your existing styles) */
        .fc-header {
          background-color: #5755FF !important;
          min-height: 97px;
          border-radius: 0px 0px 20px 20px;
          font-family: sans-serif;
        }
        .chat-fc-form-outer p.fc-header {
          /* border: 2px solid black; */
        }
        .chat-fc-form-outer div.fc-form ul li {
          margin-bottom: 20px !important;
        }
        .chat-fc-form-outer div.fc-form ul li input {
          padding: 5px !important;
          box-shadow: none;
          height: 48px;
          
        }
        .chat-fc-form-outer div.fc-form a.fc-button {
          background-color: #5755FF !important;
        }
        .fc-form p {
          font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Fira Sans, Droid Sans, Cantarell, sans-serif;
          line-height: 1.42;
          font-weight: normal;
        }
        .chat-fc-form-outer p {
          font-weight: 300 !important;
          padding-top: 27px !important;
          font-family: sans-serif;
        }

       
        
      `}</style>
    </>
  );
};

export default CustomScripts;